import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { LoginAttemptModel, PaginatedResultModel } from '@data-layer/models/common';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumPasswordLabel } from '@enum/enumPasswordLabel';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { CUSTOM_MASK, getPasswordErr } from '@helpers/custom-validator';
import { Observable, ReplaySubject } from 'rxjs';
import { CompaniesService } from '@services/companies.service';
import { AuthService } from '@app/modules/auth/services';
import { Router } from '@angular/router';
import { GenericService } from '@services/generic.service';
import { UsersRepository } from '@data-layer/repositories';
import { UserBlockPossibilityModel } from '@data-layer/models/user';

@Component({
  selector: 'account-settings-user-details-page',
  templateUrl: './account-settings-user-details-page.component.html',
  styleUrls: ['./account-settings-user-details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsUserDetailsPageComponent implements OnInit {
  public loginAttempts$: Observable<LoginAttemptModel[]>;

  public isChangePassword: boolean = false;
  public isShowLoginAttemptsList: boolean = false;
  public isDeactivateAccount: boolean = false;
  public blockOptions: UserBlockPossibilityModel;
  public passwordFormGroup: UntypedFormGroup;
  public deletionReasonControl: UntypedFormControl = new UntypedFormControl();

  public LOGIN_ATTEMPTS_NUMBER: number = 10;
  public CUSTOM_MASK = CUSTOM_MASK;
  public passwordLabels: Array<{
    code: enumPasswordLabel;
    isError: boolean;
    text: string;
  }> = [
    {
      code: enumPasswordLabel.COUNT,
      isError: false,
      text: 'PASSWORD_COUNT',
    },
    {
      code: enumPasswordLabel.UPPER_LETTER,
      isError: false,
      text: 'PASSWORD_UPPER_LETTER',
    },
    {
      code: enumPasswordLabel.LOWER_LETTER,
      isError: false,
      text: 'PASSWORD_LOWER_LETTER',
    },
    {
      code: enumPasswordLabel.INC_NUMBER,
      isError: false,
      text: 'PASSWORD_INC_NUMBER',
    },
  ];
  public confirmPasswordErrors: { [key: string]: string } = {
    required: 'FORM_ERROR.REQUIRED',
    equals: 'FORM_ERROR.PASSWORD_NOT_EQUAL',
  };
  public isTablet$: Observable<boolean>;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private genericService: GenericService,
    private usersRepository: UsersRepository,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.isTablet$ = this.genericService.isTablet$;
    this.loginAttempts$ = this.companiesService.getUserLoginAttempts(0, this.LOGIN_ATTEMPTS_NUMBER).pipe(
      map((attempts: PaginatedResultModel<LoginAttemptModel>) => attempts.items),
      shareReplay(),
    );

    this.createPasswordForm();

    this.passwordFormGroup
      .get('password')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((actualValue: string) => {
        this.generatePasswordLabels(actualValue);
        if (this.passwordFormGroup.get('confirmPassword').touched) {
          this.comparePasswords(actualValue, false);
        }
      });
    this.passwordFormGroup
      .get('confirmPassword')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((actualValue: string) => this.comparePasswords(actualValue, true));
  }

  public changePassword(): void {
    this.authService
      .changePassword(this.passwordFormGroup.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.changeIsChangePasswordState());
  }

  public openDeactivateAccountModal(): void {
    this.usersRepository.isBlockAvailableForUser().subscribe((blockOptions: UserBlockPossibilityModel) => {
      this.blockOptions = blockOptions;
      this.isDeactivateAccount = true;
      this.cdr.markForCheck();
    });
  }

  public cancelAccount(): void {
    this.usersRepository
      .blockAccountByUser(this.deletionReasonControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.router.navigate(['auth', 'login']);
      });
  }

  public changeIsChangePasswordState(): void {
    this.isChangePassword = !this.isChangePassword;
    this.cdr.markForCheck();
  }

  public get isAnyPasswordRuleInvalid(): boolean {
    return this.passwordLabels.some((pl) => pl.isError);
  }

  private createPasswordForm(): void {
    this.passwordFormGroup = this.fb.group({
      oldPassword: new UntypedFormControl(null, [Validators.required]),
      password: new UntypedFormControl(null, [Validators.required]),
      confirmPassword: new UntypedFormControl(null, [Validators.required]),
    });
  }

  private comparePasswords(actualValue: string, isConfirmPassword: boolean): void {
    const password: string = isConfirmPassword ? this.passwordFormGroup.value.password : this.passwordFormGroup.value.confirmPassword;
    const confirmPasswordControl: AbstractControl = this.passwordFormGroup.get('confirmPassword');
    if (actualValue !== password) {
      confirmPasswordControl.setErrors({ equals: true });
      confirmPasswordControl.markAsTouched();
    } else {
      confirmPasswordControl.setErrors(null);
    }
  }

  private generatePasswordLabels(actualValue: string): void {
    this.passwordLabels.forEach((l: any) => (l.isError = getPasswordErr(actualValue, l.code)));
  }
}
